// -----------------------------------------------------------------------------
// ui/panels/media.ts — import media + the media library.
//
// Click or drop to import video/images. Files are probed (via the engine's media
// manager) into assets, persisted to scratch storage, and listed here. Clicking a
// library item appends it to the timeline. Everything is local — the hint makes
// the "nothing is uploaded" promise explicit. (CLAUDE.md §6 Tier 0: media-panel
// import.)
// -----------------------------------------------------------------------------
import { AddClipCommand } from '../../core/commands/clip-commands';
import { AddTrackCommand } from '../../core/commands/track-commands';
import {
  type MediaAsset,
  type MediaClip,
  clipEnd,
  findTrack,
  newId,
} from '../../core/model';
import { formatTimecode } from '../../shared/time';
import type { EditorContext } from '../context';
import { clear, el } from '../dom';

const IMAGE_DEFAULT_DURATION = 5; // seconds for a still on the timeline

export function createMediaPanel(ctx: EditorContext): HTMLElement {
  const { store } = ctx;

  const fileInput = el('input', {
    type: 'file',
    accept: 'video/*,image/*',
    multiple: true,
    style: 'display:none',
  }) as HTMLInputElement;

  const dropzone = el(
    'div',
    { class: 'zf-dropzone', onclick: () => fileInput.click() },
    'Click to import, or drop video / images here',
  );
  dropzone.addEventListener('dragover', (e) => {
    e.preventDefault();
    dropzone.classList.add('dragover');
  });
  dropzone.addEventListener('dragleave', () => dropzone.classList.remove('dragover'));
  dropzone.addEventListener('drop', (e) => {
    e.preventDefault();
    dropzone.classList.remove('dragover');
    void importFiles((e as DragEvent).dataTransfer?.files ?? null);
  });
  fileInput.addEventListener('change', () => {
    void importFiles(fileInput.files);
    fileInput.value = ''; // allow re-importing the same file
  });

  const grid = el('div', { class: 'zf-media-grid' });

  const panel = el(
    'div',
    { class: 'zf-panel' },
    el('h2', {}, 'Media'),
    el('p', { class: 'zf-panel-hint' }, 'Your media stays on your device — nothing is uploaded.'),
    dropzone,
    fileInput,
    grid,
  );

  renderGrid();
  store.subscribe(renderGrid);

  async function importFiles(files: FileList | null): Promise<void> {
    if (!files || files.length === 0) return;
    for (const file of Array.from(files)) {
      try {
        ctx.setStatus(`Importing ${file.name}…`);
        const asset = await ctx.media.importFile(file);
        // Persist the bytes so a saved project can re-open this media later.
        try {
          await ctx.scratch.put(asset.id, file);
          asset.scratchKey = asset.id;
        } catch {
          /* scratch is best-effort; import still works in-memory */
        }
        store.addAsset(asset);
        ctx.setStatus(`Imported ${asset.name}.`);
      } catch (err) {
        ctx.setStatus(err instanceof Error ? err.message : 'Import failed.', 'error');
      }
    }
  }

  function renderGrid(): void {
    clear(grid);
    if (store.state.assets.length === 0) {
      grid.append(el('div', { class: 'zf-panel-hint' }, 'No media yet.'));
      return;
    }
    for (const asset of store.state.assets) grid.append(mediaItem(asset));
  }

  function mediaItem(asset: MediaAsset): HTMLElement {
    const item = el(
      'div',
      { class: 'zf-media-item', title: 'Click to add to the timeline' },
      el('div', { class: 'zf-media-thumb' }, asset.kind === 'image' ? '🖼' : '🎞'),
      el('div', { class: 'zf-media-name' }, asset.name),
      el('div', { class: 'zf-media-meta' }, metaText(asset)),
    );
    item.addEventListener('click', () => addToTimeline(asset));
    return item;
  }

  function metaText(asset: MediaAsset): string {
    const size = `${asset.width}×${asset.height}`;
    return asset.kind === 'image' ? size : `${size} · ${formatTimecode(asset.durationSec)}`;
  }

  function addToTimeline(asset: MediaAsset): void {
    const project = store.state.project;
    let track = project.tracks.find((t) => t.kind === 'video');
    if (!track) {
      const addTrack = new AddTrackCommand('video', 'Video 1', 0);
      store.dispatch(addTrack);
      track = findTrack(project, addTrack.trackId);
    }
    if (!track) return;

    const end = track.clips.reduce((max, c) => Math.max(max, clipEnd(c)), 0);
    const duration = asset.kind === 'image' ? IMAGE_DEFAULT_DURATION : Math.max(0.1, asset.durationSec);
    const clip: MediaClip = {
      id: newId(),
      kind: asset.kind === 'image' ? 'image' : 'video',
      name: asset.name,
      startSec: end, // append after existing clips so nothing overlaps
      durationSec: duration,
      assetId: asset.id,
      inSec: 0,
    };
    store.dispatch(new AddClipCommand(track.id, clip));
    store.select(clip.id);
    ctx.setStatus(`Added “${asset.name}” to the timeline.`);
  }

  return panel;
}
